<template>
  <section id="solutions" class="ec-section solutions">
    <div class="solutions__bg" aria-hidden="true"></div>
    <div class="ec-container">
      <header class="solutions__head">
        <span class="ec-eyebrow" v-reveal>{{ $t('solutions.eyebrow') }}</span>
        <h2 class="ec-title" v-reveal="60">{{ $t('solutions.titlePre') }}<span class="ec-gradient-text">{{ $t('solutions.titleHi') }}</span></h2>
        <p class="ec-lead" v-reveal="120">{{ $t('solutions.lead') }}</p>
      </header>

      <div class="solutions__layout" v-reveal="80">
        <div class="tabs" role="tablist">
          <button
            v-for="(s, i) in solutions"
            :key="s.name"
            :class="['tab', { active: active === i }]"
            role="tab"
            :aria-selected="active === i"
            @click="active = i"
          >
            <span class="tab__icon">{{ s.icon }}</span>
            <span class="tab__name">{{ s.name }}</span>
          </button>
        </div>

        <div class="panel" :key="active">
          <div class="panel__text">
            <h3>{{ current.title }}</h3>
            <p>{{ current.desc }}</p>
            <ul>
              <li v-for="point in current.points" :key="point">
                <span class="check">✓</span>{{ point }}
              </li>
            </ul>
            <div class="panel__metrics">
              <div v-for="m in current.metrics" :key="m.label">
                <strong class="ec-gradient-text">{{ m.value }}</strong>
                <span>{{ m.label }}</span>
              </div>
            </div>
            <a href="#contact" class="ec-btn ec-btn-primary">{{ $t('solutions.cta') }}</a>
          </div>
          <div class="panel__art" :style="{ background: current.grad }">
            <span class="panel__art-icon">{{ current.icon }}</span>
            <div class="panel__art-rings"><i></i><i></i><i></i></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// Visual attributes per industry; localized text merged from i18n by index.
const META = [
  { icon: '🌐', grad: 'linear-gradient(150deg,#FF9900,#FF6F00)' },
  { icon: '🎮', grad: 'linear-gradient(150deg,#232F3E,#FF9900)' },
  { icon: '🏧', grad: 'linear-gradient(150deg,#527FFF,#232F3E)' },
  { icon: '📡', grad: 'linear-gradient(150deg,#FF9900,#527FFF)' },
  { icon: '🛒', grad: 'linear-gradient(150deg,#FF6F00,#FF9900)' }
]

export default {
  name: 'SolutionsSection',
  data() {
    return { active: 0 }
  },
  computed: {
    solutions() {
      return this.$tm('solutions.items').map((item, i) => ({ ...META[i], ...item }))
    },
    current() { return this.solutions[this.active] }
  }
}
</script>

<style scoped>
.solutions { overflow: hidden; }
.solutions__bg { position: absolute; inset: 0; background: radial-gradient(ellipse 60% 50% at 80% 0%, rgba(255,153,0,.1), transparent 60%); }
.solutions__head { text-align: center; display: flex; flex-direction: column; align-items: center; margin-bottom: 54px; position: relative; }

.solutions__layout { position: relative; }
.tabs { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; margin-bottom: 34px; }
.tab {
  display: inline-flex; align-items: center; gap: 9px;
  padding: 12px 22px; border-radius: 999px;
  background: rgba(255,255,255,.04); border: 1px solid var(--ec-line);
  color: var(--ec-text-soft); font-size: 15px; font-weight: 600;
  transition: all .25s ease;
}
.tab:hover { color: var(--ec-text); border-color: rgba(255,153,0,.4); }
.tab.active { color: #04101f; background: var(--ec-grad); border-color: transparent; box-shadow: 0 12px 30px -12px rgba(255,153,0,.6); }
.tab__icon { font-size: 18px; }

.panel {
  display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 40px; align-items: stretch;
  background: var(--ec-card); border: 1px solid var(--ec-line); border-radius: 24px;
  padding: 44px; box-shadow: var(--ec-shadow);
  animation: fade .5s ease both;
}
@keyframes fade { from { opacity: 0; transform: translateY(16px); } }

.panel__text h3 { font-size: 26px; margin-bottom: 14px; }
.panel__text > p { color: var(--ec-text-soft); font-size: 16px; margin-bottom: 22px; }
.panel__text ul { list-style: none; padding: 0; margin: 0 0 26px; display: flex; flex-direction: column; gap: 12px; }
.panel__text li { display: flex; align-items: center; gap: 12px; font-size: 15px; color: var(--ec-text); }
.check { display: grid; place-items: center; width: 22px; height: 22px; border-radius: 50%; background: var(--ec-grad-soft); color: var(--ec-cyan); font-size: 12px; font-weight: 800; flex: none; }

.panel__metrics { display: flex; gap: 40px; margin-bottom: 30px; }
.panel__metrics strong { font-size: 32px; font-weight: 800; display: block; }
.panel__metrics span { font-size: 13px; color: var(--ec-text-mute); }

.panel__art {
  position: relative; border-radius: 18px; display: grid; place-items: center;
  min-height: 320px; overflow: hidden;
}
.panel__art-icon { font-size: 90px; filter: drop-shadow(0 10px 30px rgba(0,0,0,.4)); position: relative; z-index: 2; }
.panel__art-rings i { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border: 1.5px solid rgba(255,255,255,.35); border-radius: 50%; }
.panel__art-rings i:nth-child(1) { width: 180px; height: 180px; animation: spin 12s linear infinite; }
.panel__art-rings i:nth-child(2) { width: 270px; height: 270px; border-style: dashed; animation: spin 20s linear infinite reverse; }
.panel__art-rings i:nth-child(3) { width: 360px; height: 360px; border-color: rgba(255,255,255,.18); }
@keyframes spin { to { transform: translate(-50%,-50%) rotate(360deg); } }

@media (max-width: 860px) {
  .panel { grid-template-columns: 1fr; padding: 30px; }
  .panel__art { min-height: 220px; order: -1; }
  .panel__art-icon { font-size: 64px; }
}
</style>
